import { AxiosResponse } from 'axios';
import { config } from '../config';
import axios from './axios';
import { API, CLIENT, ITEMS, REPLACEMENTS, SETS } from './endpoints';

const backendUrl: string = config.backendApiUrl;
const baseUrl = `${backendUrl}/${API}/${CLIENT}/${REPLACEMENTS}`;

export interface IReplacementWord {
  text: string;
  spaceBefore: boolean;
}

export interface INewReplacement {
  source: IReplacementWord[];
  target: IReplacementWord;
}

export const getReplacements = () => {
  return axios.get(baseUrl);
};

export const getReplacement = (id: number) => {
  return axios.get(`${baseUrl}/${id}`);
};

export const deleteReplacement = (id: number) => {
  return axios.delete(`${baseUrl}/${id}`);
};

export const patchReplacementSet = (args: Partial<IReplacementSetPatchOut>) => {
  const { id, name, isShared } = args;

  return axios.patch(`${backendUrl}/${API}/${REPLACEMENTS}/${SETS}/${id}`, { name, isShared });
};

export const postNewReplacement = (newReplacement: INewReplacement) => {
  return axios.post(`${baseUrl}`, newReplacement, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export enum ReplacementSetType {
  PERSONAL = 'PERSONAL',
  SHARED = 'SHARED',
  GLOBAL = 'GLOBAL',
}
export interface IReplacementSet {
  id: number;
  name: string;
  type: ReplacementSetType;
  isOwner: boolean;
}

export interface IEntryValue {
  spaceBefore: boolean | undefined;
  text: string;
}
export interface IEntryItem {
  id: number;
  source: IEntryValue[];
  target: IEntryValue;
}

export interface IReplacementSetCreateOut {
  isShared: boolean;
  name: string;
}

export interface IReplacementSetPatchOut {
  id: number;
  isShared: boolean;
  name: string;
}

export interface IReplacementSetCreateIn {
  id: number;
}

export type ReplacementSetItems = IEntryItem[];
export interface IReplacementSetCreateItemsOut {
  id: number;
  items: ReplacementSetItems;
}

export interface IReplacementSetUpdateItemsOut {
  id: number;
  toCreateItems: ReplacementSetItems;
  toUpdateItems: ReplacementSetItems;
  toDeleteIndexes: number[];
}

export type LocalReplacementSets = { checked: boolean; set: IReplacementSet }[];
export type ReplacementSets = IReplacementSet[];
export const getReplacementSets = (): Promise<AxiosResponse<ReplacementSets>> => {
  return axios.get(`${backendUrl}/${API}/${REPLACEMENTS}/${SETS}`);
};

export const createReplacementSet = ({
  name,
  isShared,
}: IReplacementSetCreateOut): Promise<AxiosResponse<IReplacementSetCreateIn>> => {
  return axios.post(`${backendUrl}/${API}/${REPLACEMENTS}/${SETS}`, { name, isShared });
};

export const createReplacementSetItems = ({ id, items }: IReplacementSetCreateItemsOut) => {
  const requests = items.map((item) =>
    axios.post(`${backendUrl}/${API}/${REPLACEMENTS}/${SETS}/${id}/${ITEMS}`, {
      source: item.source,
      target: item.target,
      isCaseSensitive: false,
    })
  );
  return Promise.all(requests);
};

export const updateReplacementSetItems = ({
  id,
  toCreateItems,
  toUpdateItems,
  toDeleteIndexes,
}: IReplacementSetUpdateItemsOut) => {
  const createRequests = toCreateItems.map(
    (item) => () =>
      axios.post(`${backendUrl}/${API}/${REPLACEMENTS}/${SETS}/${id}/${ITEMS}`, {
        source: item.source,
        target: item.target,
        isCaseSensitive: false,
      })
  );

  const updateRequests = toUpdateItems.map(
    (item) => () =>
      axios.patch(`${backendUrl}/${API}/${REPLACEMENTS}/${SETS}/${id}/${ITEMS}/${item.id}`, {
        source: item.source,
        target: item.target,
        isCaseSensitive: false,
      })
  );

  const deleteRequests = toDeleteIndexes.map(
    (item) => () => axios.delete(`${backendUrl}/${API}/${REPLACEMENTS}/${SETS}/${id}/${ITEMS}/${item}`)
  );
  const allRequests = deleteRequests.concat(updateRequests).concat(createRequests);

  return allRequests;
};

export const deleteReplacementSet = (id: number) => {
  return axios.delete(`${backendUrl}/${API}/${REPLACEMENTS}/${SETS}/${id}`);
};

export const getReplacementSet = (id: number) => {
  return axios.get(`${backendUrl}/${API}/${REPLACEMENTS}/${SETS}/${id}`);
};

export const getReplacementSetItems = (setId: number): Promise<AxiosResponse<ReplacementSetItems>> => {
  return axios.get(`${backendUrl}/${API}/${REPLACEMENTS}/${SETS}/${setId}/${ITEMS}`);
};
